import { faLockAlt } from "@jewlr/pro-light-svg-icons/faLockAlt"
import {
  Box,
  Button,
  Divider,
  Flex,
  FontAwesomeIcon,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { useState } from "react"
import styled from "styled-components"

import api from "apis"
import { history } from "app-store-v2"
import { useAddonCart } from "contexts/addon-cart"
import { IS_NA } from "helpers/application"
import { to_currency } from "helpers/number"

import AddonItem from "./addon-item"
import LineItem from "./line-item"

const SummaryRow = styled(Flex)`
  font-size: 16px;
  justify-content: space-between;
  line-height: 21px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
  span {
    font-weight: 700;
  }
`

const CheckoutButton = styled(Button).attrs({
  variant: "checkout",
})`
  border-radius: 5px;
  font-size: 18px;
  font-weight: 700;
  line-height: 16px;
  height: 44px;
  padding: 0;
  width: 100%;
`

const Cart = ({ line_items }) => {
  const {
    addonItems,
    addGiftWrap,
    addGreetingCard,
    removeAllAddonsWithType,
  } = useAddonCart()

  const [loading, setLoading] = useState(false)
  const [errMsg, setErrMsg] = useState("")

  const subtotal = addonItems.reduce(
    (accu, addonItem) => accu + (addonItem.price || 0),
    0
  )

  const handleCheckout = async () => {
    try {
      setLoading(true)
      const url = "/secure/api/v10/order/purchase_gift_packaging"
      const body = { addons: addonItems }
      const response = await api.post(url, body)
      setLoading(false)
      if (response.success) {
        history.push(response.data.redirect_url)
      } else {
        throw new Error("Error adding gift packaging")
      }
    } catch (err) {
      setLoading(false)
      console.log(err?.errors)
      setErrMsg(err?.errors ? err?.errors[0] : err)
    }
  }

  const handleGiftWrap = (line_item, wrap) => {
    const addon_type = "PACKAGING"
    if (wrap && wrap.id === "NONE") {
      removeAllAddonsWithType(line_item.id, addon_type)
    } else if (wrap) {
      addGiftWrap(wrap, {
        added_from: "post_purchase_page",
        addon_type,
        lid: line_item.id,
      })
    }
  }

  const handleGreetingCard = (line_item, card, callbacks) => {
    const addon_type = "GREETING_CARD"
    if (!card) {
      removeAllAddonsWithType(line_item.id, addon_type)
    } else {
      addGreetingCard(card, {
        added_from: "post_purchase_page",
        addon_type,
        lid: line_item.id,
      })
    }
    if (callbacks && callbacks.onSuccess) {
      callbacks.onSuccess()
    }
  }

  return (
    <>
      <Box>
        {line_items
          .filter(
            ({ can_add_greeting_card, can_add_gift_wrap }) =>
              can_add_greeting_card || can_add_gift_wrap
          )
          .map((line_item) => (
            <LineItem
              handleGiftWrap={handleGiftWrap}
              handleGreetingCard={handleGreetingCard}
              key={line_item.id}
              line_item={line_item}
            />
          ))}
        {addonItems.map((addonItem) => (
          <AddonItem
            addonItem={addonItem}
            handleGreetingCard={handleGreetingCard}
            key={`${addonItem.id}`}
            lineItems={line_items}
          />
        ))}
        {addonItems.length > 0 && <Divider bg="grey.20" my={2} />}
        <Flex justifyContent={{ _: "center", desktop: "flex-end" }} mt={2}>
          <Box width={{ _: "100%", desktop: "320px" }}>
            <Box mb={2} py="12px">
              <SummaryRow>
                <span>Subtotal:</span>
                <Typography>
                  {to_currency(subtotal, { precision: 2 })}
                </Typography>
              </SummaryRow>
              {IS_NA() && (
                <SummaryRow>
                  <span>Sales Tax:</span>
                  <Typography>Calculated at checkout</Typography>
                </SummaryRow>
              )}
            </Box>
            <CheckoutButton
              disabled={!addonItems.length}
              loading={loading}
              onClick={() => handleCheckout()}
              startIcon={
                <FontAwesomeIcon
                  fontSize="12px"
                  icon={faLockAlt}
                  style={{ verticalAlign: "baseline" }}
                />
              }
            >
              Checkout
            </CheckoutButton>
            {errMsg && (
              <Typography color="error" mt={1}>
                {errMsg}
              </Typography>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  )
}

Cart.propTypes = {
  line_items: PropTypes.array,
}

export default Cart
